'use client';

import type { Ref } from 'react';
import { Checkbox, Fieldset, Stack } from '@pie/amber';
import type { ConsentTypeCode } from '@/lib/api/consents/types';
import type { ConsentFormValues } from './formValues';
import { MobileNumberField } from './MobileNumberField';

type Props = {
  values: ConsentFormValues;
  showElectronicDelivery: boolean;
  showTextAlerts: boolean;
  showAutomatedCalling: boolean;
  onToggle: (consentType: ConsentTypeCode) => void;
  phoneRef?: Ref<HTMLInputElement>;
  phoneError: string | null;
  onPhoneChange: (value: string) => void;
};

export const CommunicationPreferences = ({
  values,
  showElectronicDelivery,
  showTextAlerts,
  showAutomatedCalling,
  onToggle,
  phoneRef,
  phoneError,
  onPhoneChange,
}: Props) => {
  if (!showTextAlerts && !showAutomatedCalling) return null;

  const showAll = showElectronicDelivery && (showTextAlerts || showAutomatedCalling);
  const onCheck = (consentType: ConsentTypeCode) => () => onToggle(consentType);

  const checkboxes = (
    <Stack gap="2xs">
      {showTextAlerts && (
        <Checkbox
          checked={values.textAlerts}
          onCheckedChange={onCheck('TRANSACTIONAL_SMS')}
          label="Text account alerts"
          helpText={
            'By checking this box, you agree to receive recurring automated text messages from Pie Insurance using an automatic telephone dialing system regarding your insurance policy and account servicing. You also agree to the Mobile Terms and Privacy Policy. Consent is not required to purchase insurance. Message frequency varies. Message and data rates may apply. Reply STOP to 280106 to opt out or HELP for assistance..'
          }
        />
      )}
      {showAutomatedCalling && (
        <Checkbox
          checked={values.automatedCalling}
          onCheckedChange={onCheck('AI_CALLING')}
          label="Automated calling account alerts"
          helpText={
            'By checking this box, you agree to receive automated phone calls, including AI-generated or prerecorded voice calls, from Pie Insurance using an automatic telephone dialing system regarding your insurance policy and account servicing. You also agree to the Mobile Terms and Privacy Policy. Consent is not required to purchase insurance. Call frequency varies. Message and data rates may apply. You may opt out at any time by pressing 0 on the keypad during an automated call.'
          }
        />
      )}
    </Stack>
  );

  return (
    <>
      {showAll ? <Fieldset legend="Communication preferences">{checkboxes}</Fieldset> : checkboxes}
      <MobileNumberField
        ref={phoneRef}
        value={values.mobilePhone}
        error={phoneError}
        onChange={onPhoneChange}
      />
    </>
  );
};
